/**
 * @file 合作页面申请表单
 */

import React, { ReactNode } from 'react'
import { observer } from 'mobx-react'
import { FormState, FieldState } from 'formstate-x'
import { useFormstateX, Form, FormItem, TextInput, CheckboxGroup, Checkbox, RadioGroup, Radio } from 'react-icecream-2/lib/form-x'

import { applyCooperation, ApplyCooperationOptipns } from 'apis/business'
import { arrayNotEmpty, textNotEmpty, textOfPattern } from 'utils/form'
import Button from 'components/UI/Button'
import Section from 'components/Section'
import { useMobile } from 'hooks/ua'
import { useApiWithDialogToaster } from 'hooks/api'
import Intro from '../Intro'
import Contact from '../Contact'

import styles from './style.less'

const validateRequired = textNotEmpty('请填写')
const validatePhone = textOfPattern(/^\d{11}$/, '请填写正确的手机号')
const validateArrayRequired = arrayNotEmpty('请选择')

const additionalISPLabel = '其他'

function createState() {
  const ispField = new FieldState<string[]>([]).validators(validateArrayRequired)
  return new FormState({
    contactName: new FieldState('').validators(validateRequired),
    contactPhone: new FieldState('').validators(validateRequired, validatePhone),
    wechat: new FieldState(''),
    ISP: ispField,
    additionalISP: new FieldState('')
      .validators(validateRequired)
      .disableValidationWhen(() => !ispField.value.includes(additionalISPLabel)),
    networkType: new FieldState([]).validators(validateArrayRequired),
    usbwDesc: new FieldState('').validators(validateRequired),
    magnitudeBW: new FieldState('1-10G').validators(validateRequired),
    provinces: new FieldState('').validators(validateRequired),
    source: new FieldState('')
  })
}

export type State = ReturnType<typeof createState>

export function getValue(state: State): ApplyCooperationOptipns {
  const { ISP, additionalISP, networkType, ...rest } = state.value
  const realISP = ISP.includes(additionalISPLabel)
    ? ISP.filter(it => it !== additionalISPLabel).concat(additionalISP)
    : ISP

  return {
    ISP: realISP.join(','),
    networkType: networkType.join(','),
    ...rest
  }
}

const ispOptions = ['电信', '移动', '联通', additionalISPLabel]
const networkTypeOptions = ['公网', '内网']
const bandwidthOptions = ['1-10G', '10G-30G', '30G以上']
const sourceOptions = ['官网', '微信公众号', '朋友推荐', 'QQ群', 'NiuLink 商务主动联系']

export default observer(function ApplyForm() {
  const isMobile = useMobile()
  const state = useFormstateX(createState, [])
  const fields = state.$

  const {
    call: submit,
    loading: isSubmitting,
    Dialog
  } = useApiWithDialogToaster(applyCooperation, '提交成功，我们将于 3-5 个工作日内联系您！', '提交失败，请稍后重试')

  async function handleSubmit() {
    await submit(getValue(state))
    state.reset()
  }

  const inputStyle = isMobile ? { width: '100%' } : {}

  return (
    <FormWrap>
      <Form
        className={styles.wrapper}
        state={state}
        onSubmit={handleSubmit}
        layout={isMobile ? 'vertical' : 'horizontal'}
        labelWidth="224px"
        labelAlign="right"
        footer={null}
      >
        <FormItem label="联系人姓名" required>
          <TextInput state={fields.contactName} placeholder="请输入您的姓名" style={inputStyle} />
        </FormItem>
        <FormItem label="联系电话" required>
          <TextInput state={fields.contactPhone} placeholder="请输入您的联系电话" style={inputStyle} />
        </FormItem>
        <FormItem label="微信号">
          <TextInput state={fields.wechat} style={inputStyle} />
        </FormItem>
        <FormItem label="您现在持有的带宽资源是" required labelVerticalAlign="text">
          <CheckboxGroup state={fields.ISP}>
            {ispOptions.map(v => <Checkbox key={v} value={v}>{v}</Checkbox>)}
          </CheckboxGroup>
        </FormItem>
        {fields.ISP.value.includes(additionalISPLabel) && (
          <FormItem label="">
            <TextInput state={fields.additionalISP} placeholder="请输入" />
          </FormItem>
        )}
        <FormItem label="请问您的网络类型是" required labelVerticalAlign="text">
          <CheckboxGroup state={fields.networkType}>
            {networkTypeOptions.map(v => <Checkbox key={v} value={v}>{v}</Checkbox>)}
          </CheckboxGroup>
        </FormItem>
        <FormItem label="您的带宽资源单条上行多大" required>
          <TextInput state={fields.usbwDesc} placeholder="如：单线100m，单线1g等" style={inputStyle} />
        </FormItem>
        <FormItem label="请问您的带宽资源量级是多少" required labelVerticalAlign="text">
          <RadioGroup state={fields.magnitudeBW}>
            {bandwidthOptions.map(v => <Radio key={v} value={v}>{v}</Radio>)}
          </RadioGroup>
        </FormItem>
        <FormItem label="请问您现持有的资源属于哪个省份" required>
          <TextInput state={fields.provinces} placeholder="如：广东；福建（多个省份用分号隔开）" style={inputStyle} />
        </FormItem>
        <FormItem label="请问您是从什么渠道了解到我们" className={styles.sourceField} labelVerticalAlign="text">
          <RadioGroup state={fields.source}>
            {sourceOptions.map(v => <Radio value={v} key={v}>{v}</Radio>)}
          </RadioGroup>
        </FormItem>
        <div className={styles.submitLine}>
          <Button size={isMobile ? 'default' : 'large'} type="primary" htmlType="submit" disabled={isSubmitting}>
            {isSubmitting ? '提交中...' : '提交'}
          </Button>
        </div>
      </Form>
      <Dialog />
    </FormWrap>
  )
})

type FormWrapProps = {
  children: ReactNode
}

const FormWrap = observer(function _FormWrap({ children }: FormWrapProps) {
  const isMobile = useMobile()

  return (
    <Section
      name="apply"
      title="如果满足以下任意条件，请联系我们"
      grey={!isMobile}
      wrapClassName={styles.sectionWrap}
      withTailPadding
    >
      <Intro />
      <div className={styles.formWrap}>
        <div className={styles.formHeader}>
          <div className={styles.title}>申请加入合作渠道</div>
          {!isMobile && (
            <div className={styles.subTitle}>
              如果您拥有闲置带宽资源、或拥有自有平台，又或是资源招募爱好者均可与我们取得联系，获取资源支持，获取稳定收益。
            </div>
          )}
        </div>
        <div className={styles.formContent}>{children}</div>
      </div>
      <Contact />
    </Section>
  )
})
